import { create } from 'zustand';
import type { User } from './types';
import { fetchUserProfile, getCurrentSessionUser, loginWithPassword, logoutUser } from './api';
import { seedPilotTemplates } from '../sync/db';
import { isLocalDemoAuthEnabled, isSupabaseConfigured, requireSupabase } from './supabaseClient';
import { useOrderStore } from '../orders/orderStore';
import { useSyncStore } from '../sync/syncStore';
import { syncMembershipStoreFromUser, useMembershipStore } from './membershipStore';
// 1.19.8 — Тянет реквизиты компании при логине, чтобы PaymentScreen
// работал офлайн (СБП/QR номер и банковские реквизиты должны быть
// доступны технику без сети).
import { hydrateCompanyInfo } from '../invoices/api';
import { hydrateAcquiringSettings } from '../acquiring/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  // 1.30.0 / P0-4 — Активна ли recovery-сессия (юзер пришёл по
  // ссылке «сбросить пароль»). Если true, гарды роутинга должны
  // удерживать его на /reset-password, не давая разъехаться по
  // ролевым областям. См. authStore.bindAuthListener и AuthBoundary.
  recoveryActive: boolean;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

let authListenerBound = false;
let sessionWatcher: number | null = null;

function resetSessionScopedStores() {
  useOrderStore.getState().reset();
  useMembershipStore.getState().clearMemberships();
  useSyncStore.setState({
    status: navigator.onLine ? 'synced' : 'offline',
    pendingCount: 0,
    failed: [],
    lastSyncAt: null,
  });
}

function applyUserState(set: (patch: Partial<AuthState>) => void, patch: Partial<AuthState>, user: User | null) {
  syncMembershipStoreFromUser(user);
  set({ ...patch, user });
  // 1.17.0a / 1.17.1 / 1.17.2 — прогрев offline-кэша бизнес-сущностей.
  // Fire-and-forget, ошибки не блокируют UI. Если оффлайн — функции
  // сами выйдут silently.
  //
  // 1.18.3 — Помечаем флагом `firstSyncInProgress` чтобы UI компоненты
  // (CustomerSearchField и т.п.) могли отличить «загрузка идёт» от
  // «локальный кэш пуст». См. syncStore.firstSyncInProgress.
  if (user?.company_id) {
    const companyId = user.company_id;
    void (async () => {
      // Лениво импортируем syncStore чтобы не утяжелять холодный старт
      // authStore. Это допустимо: задержка ~5мс при первом login.
      const { useSyncStore } = await import('../sync/syncStore');
      useSyncStore.getState().setFirstSyncInProgress(true);
      try {
        const [
          { syncCustomersFromServer },
          { syncObjectTypesFromServer, syncCustomerObjectsFromServer },
          { syncInvoicesFromServer },
          { syncAuditLogFromServer },
        ] = await Promise.all([
          import('../customers/api'),
          import('../objects/api'),
          import('../invoices/api'),
          import('../audit_log/api'),
        ]);
        // Параллельно прокачиваем ключевые сущности. bulkPut в Dexie не
        // блокирующий — все пять закончатся за < 400мс при нормальной
        // сети (после 1.18.1 incremental только новые/изменённые).
        await Promise.all([
          syncCustomersFromServer(companyId),
          syncObjectTypesFromServer(companyId),
          syncCustomerObjectsFromServer(companyId),
          syncInvoicesFromServer(companyId),
          // 1.19.0 — audit log offline. Прогрев истории событий за
          // компанию: используется в AdminOrderDetail и dispatcher
          // OrderDetail, должен быть доступен без сети.
          syncAuditLogFromServer(companyId),
        ]);
      } catch (err) {
        console.warn('[Кворум 1.17] business entities sync failed:', err);
      } finally {
        useSyncStore.getState().setFirstSyncInProgress(false);
      }
    })();
  }
}

function startSessionWatcher(set: (patch: Partial<AuthState>) => void) {
  if (!isSupabaseConfigured || sessionWatcher) return;
  sessionWatcher = window.setInterval(async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    try {
      const fresh = await getCurrentSessionUser();
      if (!fresh) {
        resetSessionScopedStores();
        set({ user: null, error: null, loading: false, initialized: true });
        return;
      }
      if (!fresh.platform_role && (!fresh.memberships || fresh.memberships.length === 0)) {
        await logoutUser().catch(() => {});
        resetSessionScopedStores();
        set({ user: null, error: 'Компания деактивирована. Сессия завершена.', loading: false, initialized: true });
        return;
      }
      applyUserState(set, { initialized: true, loading: false, error: null }, fresh);
    } catch {}
  }, 10000);
}

function bindAuthListener() {
  if (!isSupabaseConfigured || authListenerBound) return;
  authListenerBound = true;

  // Используем useAuthStore.setState напрямую, чтобы listener мог быть
  // привязан до первого вызова `initialize()` (например, при прямом
  // заходе на /reset-password — там AuthBoundary не подписывается).
  const set = (patch: Partial<AuthState>) => useAuthStore.setState(patch);

  requireSupabase().auth.onAuthStateChange((event, session) => {
    // 1.30.0 / P0-4 — Recovery-сессия.
    //
    // Supabase открывает временную авторизованную сессию, когда юзер
    // переходит по ссылке из письма «сбросить пароль». В этот момент
    // в URL висят `#access_token=...&type=recovery&...`, SDK их
    // парсит и эмитит PASSWORD_RECOVERY.
    //
    // Проблема: с точки зрения SDK эта сессия неотличима от обычной
    // (после reload или закрытия hash в URL остаётся только запись в
    // localStorage). Если мы наивно «грузим профиль → выставляем user
    // → пускаем в защищённую область», юзер окажется залогинен ДО
    // ввода нового пароля — и сможет работать дальше, не сменив его.
    //
    // Решение: ставим флаг recoveryActive вместо записи user. Гарды
    // (AuthBoundary, RoleRedirect) видят флаг и удерживают юзера
    // на /reset-password. SPA-навигация без reload, так что
    // PASSWORD_RECOVERY-контекст остаётся валидным до завершения
    // updateUser({password}).
    //
    // Флаг живёт ровно до следующего SIGNED_OUT (см. ниже), который
    // ResetPassword.tsx триггерит после успешного updateUser.
    if (event === 'PASSWORD_RECOVERY') {
      set({ recoveryActive: true, initialized: true, loading: false, error: null, user: null });
      return;
    }

    if (!session?.user) {
      // SIGNED_OUT, USER_DELETED, или нет сессии в принципе.
      // Снимаем recoveryActive — после смены пароля и signOut юзер
      // должен снова стать «гостем» и попасть на /login обычным путём.
      resetSessionScopedStores();
      set({ user: null, recoveryActive: false, initialized: true, loading: false, error: null });
      return;
    }

    void fetchUserProfile(session.user.id)
      .then((user) => {
        applyUserState(set, { initialized: true, loading: false, error: null }, user);
      })
      .catch((error) => {
        set({
          error: error instanceof Error ? error.message : 'Ошибка сессии',
          initialized: true,
          loading: false,
        });
      });
  });
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: false,
  initialized: false,
  error: null,
  recoveryActive: false,

  initialize: async () => {
    set({ loading: true, error: null });
    try {
      await seedPilotTemplates();
      bindAuthListener();
      startSessionWatcher(set);
      const user = await getCurrentSessionUser();
      applyUserState(set, { initialized: true, loading: false, error: null }, user);
      // 1.19.8 — Тянем реквизиты компании в Dexie, чтобы PaymentScreen
      // мог работать офлайн. Fire-and-forget, не блокирует UI.
      if (user?.company_id) {
        void hydrateCompanyInfo(user.company_id);
        // 1.27.0 — Также тянем acquiring_settings, чтобы PaymentScreen
        // знал офлайн, можно ли показать кнопку онлайн-оплаты.
        void hydrateAcquiringSettings(user.company_id);
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Ошибка инициализации',
        initialized: true,
        loading: false,
      });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const user = await loginWithPassword(email, password);
      if (isLocalDemoAuthEnabled) {
        localStorage.setItem('fieldsnap_local_user', JSON.stringify(user));
      }
      await seedPilotTemplates();
      bindAuthListener();
      applyUserState(set, { loading: false, initialized: true, error: null }, user);
      // 1.19.8 — Тянем реквизиты компании в Dexie, чтобы PaymentScreen
      // мог работать офлайн. Не блокируем редирект — фоновая задача.
      if (user.company_id) {
        void hydrateCompanyInfo(user.company_id);
        // 1.27.0 — Также подкачиваем acquiring_settings.
        void hydrateAcquiringSettings(user.company_id);
      }
      // Hard-redirect to the role's home route. This wipes the browser's
      // back-stack so the previous session's history doesn't leak into
      // the new one (e.g. admin pressing Back and landing on
      // /dispatch from a prior dispatcher session). Plain react-router
      // navigation can't do this — it only replaces the current entry.
      // We import lazily to keep this store free of routing knowledge
      // when called from non-browser contexts.
      try {
        const { getDefaultAppRoute } = await import('./companyRole');
        const target = getDefaultAppRoute(user, user.company_membership_id);
        window.location.replace(target);
      } catch {
        // never block login on a routing-helper import failure
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Ошибка входа', loading: false });
      throw error;
    }
  },

  logout: async () => {
    set({ loading: true, error: null });
    try {
      await logoutUser();
      resetSessionScopedStores();
      set({ user: null, recoveryActive: false, initialized: true, loading: false, error: null });
      // Same rationale as login above — wipe history on session end.
      window.location.replace('/login');
    } catch (error) {
      resetSessionScopedStores();
      set({
        user: null,
        recoveryActive: false,
        initialized: true,
        loading: false,
        error: error instanceof Error ? error.message : 'Ошибка выхода',
      });
      // Still hard-redirect on error path so the UI doesn't get stuck
      // with stale state from the previous session.
      window.location.replace('/login');
      throw error;
    }
  },
}));

// 1.30.0 / P0-4 — Eager-bind listener'а на уровне модуля.
//
// Без этого юзер, кликнувший по ссылке в письме «сбросить пароль» и
// попавший прямо на /reset-password (публичный маршрут, который НЕ
// оборачивается AuthBoundary), не получил бы обработки события
// PASSWORD_RECOVERY: AuthBoundary.initialize() — единственное место,
// где раньше вызывался bindAuthListener — на этом маршруте не
// монтируется.
//
// bindAuthListener идемпотентен (защищён флагом authListenerBound),
// так что повторный вызов из initialize() / login() ничего не делает.
//
// Это безопасный side-effect модуля: подписка делается на одиночку
// Supabase-клиента, который уже создан в supabaseClient.ts. В
// local-demo-режиме (isSupabaseConfigured === false) bindAuthListener
// выходит сразу — нет регрессии для тестовых сборок без env.
bindAuthListener();
